import React from 'react';
import { useCalculator } from '../context/CalculatorContext';
import { motion } from 'framer-motion';
import { ChevronRight, DollarSign, Plus, Minus } from 'lucide-react';
import { Language } from '../types';
import { getLanguageContent, getLanguageFromCurrency } from '../data/languages';

interface CurrencySelectorProps {
  onNext: () => void;
}

const CurrencySelector: React.FC<CurrencySelectorProps> = ({ onNext }) => {
  const { investment, updateInvestment } = useCalculator();
  
  // Get current language content
  const currentLanguage = investment.language || getLanguageFromCurrency(investment.currencyFormat);
  const content = getLanguageContent(currentLanguage);

  const handleCurrencyChange = (currency: 'MXN' | 'USD' | 'EUR') => {
    const newLanguage = getLanguageFromCurrency(currency);
    updateInvestment({ 
      currencyFormat: currency,
      language: newLanguage as Language
    });
  };

  // Handlers for increment/decrement buttons for USD
  const handleIncrementExchangeRateUSD = () => {
    if (investment.exchangeRate < 30) {
      updateInvestment({ exchangeRate: Math.round((investment.exchangeRate + 0.1) * 10) / 10 });
    }
  };

  const handleDecrementExchangeRateUSD = () => {
    if (investment.exchangeRate > 1) {
      updateInvestment({ exchangeRate: Math.round((investment.exchangeRate - 0.1) * 10) / 10 });
    }
  };

  // Handlers for increment/decrement buttons for EUR
  const handleIncrementExchangeRateEUR = () => {
    if ((investment.exchangeRateEUR || 21.70) < 35) {
      updateInvestment({ exchangeRateEUR: Math.round(((investment.exchangeRateEUR || 21.70) + 0.1) * 10) / 10 });
    }
  };

  const handleDecrementExchangeRateEUR = () => {
    if ((investment.exchangeRateEUR || 21.70) > 15) {
      updateInvestment({ exchangeRateEUR: Math.round(((investment.exchangeRateEUR || 21.70) - 0.1) * 10) / 10 });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="max-w-4xl mx-auto"
    >
      <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-8 text-white">
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
              <DollarSign className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">{content.currencySelector.title}</h2>
              <p className="text-blue-100">{content.currencySelector.subtitle}</p>
            </div>
          </div>
        </div>

        <div className="p-8 space-y-8">
          {/* Currency Selection */}
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">
                {currentLanguage === 'es' ? 'Moneda Principal' : 
                 currentLanguage === 'en' ? 'Main Currency' : 
                 'Devise Principale'}
              </h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                className={`py-6 px-6 rounded-xl text-lg font-medium transition-all duration-200 ${
                  investment.currencyFormat === 'MXN'
                    ? 'bg-gradient-to-br from-green-500 to-green-600 text-white shadow-md transform scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                onClick={() => handleCurrencyChange('MXN')}
                title="Pesos Mexicanos - Moneda base de los certificados. No requiere conversión de tipo de cambio. Ideal para inversionistas mexicanos"
              >
                <div className="text-center">
                  <div className="text-3xl mb-2">🇲🇽</div>
                  <div className="font-bold">MXN</div>
                  <div className="text-sm opacity-80">{content.currencySelector.currencyInfo.MXN.name}</div>
                </div>
              </button>
              
              <button
                className={`py-6 px-6 rounded-xl text-lg font-medium transition-all duration-200 ${
                  investment.currencyFormat === 'USD'
                    ? 'bg-gradient-to-br from-green-500 to-green-600 text-white shadow-md transform scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                onClick={() => handleCurrencyChange('USD')}
                title="Dólares Americanos - Ideal para inversionistas internacionales. Los ingresos son naturalmente dolarizados por exportación"
              >
                <div className="text-center">
                  <div className="text-3xl mb-2">🇺🇸</div>
                  <div className="font-bold">USD</div>
                  <div className="text-sm opacity-80">{content.currencySelector.currencyInfo.USD.name}</div>
                </div>
              </button>
              
              <button
                className={`py-6 px-6 rounded-xl text-lg font-medium transition-all duration-200 ${
                  investment.currencyFormat === 'EUR'
                    ? 'bg-gradient-to-br from-green-500 to-green-600 text-white shadow-md transform scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                onClick={() => handleCurrencyChange('EUR')}
                title="Euros - Para inversionistas europeos. Conversión automática de todos los valores. Los ingresos mantienen protección cambiaria"
              >
                <div className="text-center">
                  <div className="text-3xl mb-2">🇪🇺</div>
                  <div className="font-bold">EUR</div>
                  <div className="text-sm opacity-80">{content.currencySelector.currencyInfo.EUR.name}</div>
                </div>
              </button>
            </div>
          </div>

          {/* Exchange Rate for USD */}
          {investment.currencyFormat === 'USD' && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800">{content.currencySelector.exchangeRateLabels.USD}</h3>
              </div>
              
              <div className="flex items-center justify-center gap-4 mb-5">
                <button 
                  onClick={handleDecrementExchangeRateUSD}
                  className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                  disabled={investment.exchangeRate <= 1}
                >
                  <Minus className="w-5 h-5 text-gray-700" />
                </button>
                <div className="text-center">
                  <input
                    type="number"
                    min="1"
                    max="30"
                    step="0.1"
                    value={investment.exchangeRate}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value);
                      if (!isNaN(value) && value >= 1 && value <= 30) {
                        updateInvestment({ exchangeRate: value });
                      }
                    }}
                    onWheel={(e) => e.currentTarget.blur()}
                    className="w-24 text-center text-3xl font-bold text-gray-900 mb-1 border-0 focus:ring-0 focus:outline-none"
                    title={currentLanguage === 'es' ? 
                      "Tipo de cambio MXN/USD. Afecta todos los valores mostrados. Rango histórico: 15-25 MXN por USD. Actual: ~20 MXN/USD" :
                      "MXN/USD exchange rate. Affects all displayed values. Historical range: 15-25 MXN per USD. Current: ~20 MXN/USD"}
                  />
                  <div className="text-sm text-green-600">
                    {currentLanguage === 'es' ? 'MXN por USD' : 'MXN per USD'}
                  </div>
                </div>
                <button 
                  onClick={handleIncrementExchangeRateUSD}
                  className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                  disabled={investment.exchangeRate >= 30}
                >
                  <Plus className="w-5 h-5 text-gray-700" />
                </button>
              </div>

              <div className="space-y-3">
                <input
                  type="range"
                  min="1"
                  max="30"
                  step="0.1"
                  value={investment.exchangeRate}
                  onChange={(e) => updateInvestment({ exchangeRate: parseFloat(e.target.value) })}
                  className="w-full h-2 bg-gradient-to-r from-green-200 to-green-300 rounded-lg appearance-none cursor-pointer"
                  title="Desliza para ajustar tipo de cambio. Impacta todos los valores mostrados. Considera volatilidad cambiaria en tu análisis"
                />
                <div className="flex justify-between text-xs text-gray-500">
                  <span>1 MXN/USD</span>
                  <span>30 MXN/USD</span>
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm text-green-700">
                  <strong>{currentLanguage === 'es' ? 'Ejemplo:' : 'Example:'}</strong> 
                  {currentLanguage === 'es' ? 
                    ` Si el tipo de cambio es ${investment.exchangeRate}, entonces $1 USD = $${investment.exchangeRate} MXN` :
                    ` If the exchange rate is ${investment.exchangeRate}, then $1 USD = $${investment.exchangeRate} MXN`}
                </p>
              </div>
            </div>
          )}

          {/* Exchange Rate for EUR */}
          {investment.currencyFormat === 'EUR' && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800">{content.currencySelector.exchangeRateLabels.EUR}</h3>
              </div>
              
              <div className="flex items-center justify-center gap-4 mb-5">
                <button 
                  onClick={handleDecrementExchangeRateEUR}
                  className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                  disabled={(investment.exchangeRateEUR || 21.70) <= 15}
                >
                  <Minus className="w-5 h-5 text-gray-700" />
                </button>
                <div className="text-center">
                  <input
                    type="number"
                    min="15"
                    max="35"
                    step="0.1"
                    value={investment.exchangeRateEUR || 21.70}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value);
                      if (!isNaN(value) && value >= 15 && value <= 35) {
                        updateInvestment({ exchangeRateEUR: value });
                      }
                    }}
                    onWheel={(e) => e.currentTarget.blur()}
                    className="w-24 text-center text-3xl font-bold text-gray-900 mb-1 border-0 focus:ring-0 focus:outline-none"
                    title={currentLanguage === 'es' ? 
                      "Tipo de cambio MXN/EUR. Afecta todos los valores mostrados. Rango histórico: 18-25 MXN por EUR. Actual: ~22 MXN/EUR" :
                      currentLanguage === 'en' ?
                      "MXN/EUR exchange rate. Affects all displayed values. Historical range: 18-25 MXN per EUR. Current: ~22 MXN/EUR" :
                      "Taux de change MXN/EUR. Affecte toutes les valeurs affichées. Fourchette historique: 18-25 MXN par EUR. Actuel: ~22 MXN/EUR"}
                  />
                  <div className="text-sm text-purple-600">
                    {currentLanguage === 'es' ? 'MXN por EUR' : 
                     currentLanguage === 'en' ? 'MXN per EUR' : 
                     'MXN par EUR'}
                  </div>
                </div>
                <button 
                  onClick={handleIncrementExchangeRateEUR}
                  className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                  disabled={(investment.exchangeRateEUR || 21.70) >= 35}
                >
                  <Plus className="w-5 h-5 text-gray-700" />
                </button>
              </div>

              <div className="space-y-3">
                <input
                  type="range"
                  min="15"
                  max="35"
                  step="0.1"
                  value={investment.exchangeRateEUR || 21.70}
                  onChange={(e) => updateInvestment({ exchangeRateEUR: parseFloat(e.target.value) })}
                  className="w-full h-2 bg-gradient-to-r from-purple-200 to-purple-300 rounded-lg appearance-none cursor-pointer"
                  title="Ajusta el tipo de cambio MXN/EUR"
                />
                <div className="flex justify-between text-xs text-gray-500">
                  <span>15 MXN/EUR</span>
                  <span>35 MXN/EUR</span>
                </div>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg">
                <p className="text-sm text-purple-700">
                  <strong>{currentLanguage === 'es' ? 'Ejemplo:' : currentLanguage === 'en' ? 'Example:' : 'Exemple:'}</strong> 
                  {currentLanguage === 'es' ? 
                    ` Si el tipo de cambio es ${(investment.exchangeRateEUR || 21.70).toFixed(1)}, entonces €1 EUR = $${(investment.exchangeRateEUR || 21.70).toFixed(1)} MXN` :
                    currentLanguage === 'en' ?
                    ` If the exchange rate is ${(investment.exchangeRateEUR || 21.70).toFixed(1)}, then €1 EUR = $${(investment.exchangeRateEUR || 21.70).toFixed(1)} MXN` :
                    ` Si le taux de change est ${(investment.exchangeRateEUR || 21.70).toFixed(1)}, alors €1 EUR = $${(investment.exchangeRateEUR || 21.70).toFixed(1)} MXN`}
                </p>
              </div>
            </div>
          )}

          {/* Information about currency selection */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6">
            <h4 className="font-medium text-blue-800 mb-3">
              {currentLanguage === 'es' ? 'Información sobre la Moneda Seleccionada' :
               currentLanguage === 'en' ? 'Information about Selected Currency' :
               'Informations sur la Devise Sélectionnée'}
            </h4>
            <div className="space-y-2 text-sm text-blue-700">
              {content.currencySelector.currencyInfo[investment.currencyFormat].description.map((desc, index) => (
                <p key={index}>• {desc.replace('{exchangeRate}', investment.exchangeRate.toString()).replace('{exchangeRateEUR}', (investment.exchangeRateEUR || 21.70).toFixed(1))}</p>
              ))}
            </div>
          </div>

          {/* Continue Button */}
          <div className="flex justify-end">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onNext}
              className="px-8 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2"
            >
              <span>{content.common.buttons.continue}</span>
              <ChevronRight className="w-5 h-5" />
            </motion.button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default CurrencySelector;